/**
 * ExpiredLockScreen — full-screen lockout for users with expired subscriptions.
 *
 * Iter-121 (2026-05-31) — a complete pivot from the previous iter-117 →
 * iter-120 approach of letting expired users browse the portal in
 * "read-only" mode. The user explicitly chose simplification: an
 * expired user sees NOTHING except this single screen until they
 * either renew or log out.
 *
 * Two affordances only:
 *   • "Renew Subscription" → /pricing
 *   • "Log out"             → calls auth.logout()
 *
 * The screen is non-dismissible (no ESC, no X, no backdrop click) —
 * it's NOT a Radix Dialog; it's a full-page fixed overlay rendered by
 * the `ProtectedRoute` gate before any route resolves.
 *
 * Their data is intentionally kept in the database (no purging) so
 * the moment they renew they see everything exactly as they left it.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, Sparkles, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Logo } from './Logo';

export const ExpiredLockScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const goRenew = () => navigate('/pricing');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 overflow-y-auto"
      style={{ backgroundColor: '#1F1B16E5' }}  // semi-opaque dark-paper backdrop
      data-testid="expired-lock-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expired-lock-title"
    >
      <div
        className="w-full max-w-[500px] rounded-2xl p-7 md:p-9 shadow-2xl"
        style={{ backgroundColor: '#FAF7EF', border: '1px solid #E7CEC0' }}
      >
        {/* Logo + lock icon row */}
        <div className="flex items-center justify-between mb-6">
          <Logo size="md" testid="expired-lock-logo" />
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#FBF0EC', border: '1px solid #E7CEC0' }}
          >
            <Lock className="h-5 w-5" style={{ color: '#9A3412', strokeWidth: 1.8 }} />
          </div>
        </div>

        <h1
          id="expired-lock-title"
          className="text-2xl md:text-3xl font-medium mb-2"
          style={{ color: '#1C1917', fontFamily: 'Work Sans' }}
          data-testid="expired-lock-title"
        >
          Your subscription has expired
        </h1>
        {user?.email && (
          <p className="text-sm mb-4" style={{ color: '#78716C' }} data-testid="expired-lock-email">
            Signed in as <span style={{ color: '#1C1917' }}>{user.email}</span>
          </p>
        )}

        {/* The reassurance block — the heart of the new UX. */}
        <div
          className="rounded-xl p-4 mb-5 flex items-start gap-3"
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #DDEBE2' }}
          data-testid="expired-lock-reassure"
        >
          <ShieldCheck className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#1B4332', strokeWidth: 1.8 }} />
          <div>
            <p className="text-sm font-medium mb-1" style={{ color: '#1B4332' }}>
              Your data is safe &amp; secured.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#57534E' }}>
              Every portfolio, transaction, dividend, insurance policy and tax record
              you&apos;ve added is preserved exactly as it was. Renew your subscription
              and pick up right where you left off — no re-uploads, no data loss.
            </p>
          </div>
        </div>

        {/* What's unlocked */}
        <div className="mb-6">
          <p className="text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#7A5C2E' }}>
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
            Unlocked instantly on renewal
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3 text-xs" style={{ color: '#57534E' }}>
            <li className="flex items-start gap-1.5"><span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1B4332' }} /> Dashboard &amp; portfolios</li>
            <li className="flex items-start gap-1.5"><span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1B4332' }} /> Add trades &amp; dividends</li>
            <li className="flex items-start gap-1.5"><span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1B4332' }} /> Mutual funds &amp; SIPs</li>
            <li className="flex items-start gap-1.5"><span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1B4332' }} /> F&amp;O daily M2M ledger</li>
            <li className="flex items-start gap-1.5"><span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1B4332' }} /> IMAP contract-note sync</li>
            <li className="flex items-start gap-1.5"><span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1B4332' }} /> Tax reports &amp; ITR exports</li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={goRenew}
            className="flex-1 text-white text-base h-11"
            style={{ backgroundColor: '#1B4332' }}
            data-testid="expired-lock-renew-btn"
          >
            Renew Subscription
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleLogout}
            className="sm:w-auto h-11"
            data-testid="expired-lock-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
            Log out
          </Button>
        </div>

        <p className="text-[11px] text-center mt-4" style={{ color: '#9A8E78' }}>
          Need help? Email <a href="mailto:admin@equitrack.in" className="underline" style={{ color: '#1B4332' }}>admin@equitrack.in</a>.
        </p>
      </div>
    </div>
  );
};

export default ExpiredLockScreen;
